// 真實後端 Spin 服務 — 透過 GameServerClient 送 SpinCmd、收 SlotResult。
// ⚠️ 依還原 proto 實作，未對既有後端驗證；接後端時以權威 .proto 與實際 Cmd 名校正。
import * as protobuf from 'protobufjs'
import { logger } from '../../framework/core/Logger'
import { toServerBalance } from '../../framework/net/balance'
import type { GameServerClient } from '../../framework/net/GameServerClient'
import { gatewayType } from '../../framework/net/proto/gatewayProto'
import { anyType, slotResultType, spinCmdType } from '../../framework/net/proto/slotProto'
import type { BGResult, MGResult, SlotResult } from '../model/SlotResult'
import type { SpinRequest, SpinService } from './SpinService'

// 遊戲層 SpinCmd 包進 Any 的 type_url（proto 套件名 Slot001FaFaCat）。
const SPIN_DATA_TYPE_URL = 'type.googleapis.com/Slot001FaFaCat.SpinCmd'

interface RawSymbol {
  Value?: number
}
interface RawMG {
  MGSymbol?: { Reels?: { Symbols?: RawSymbol[] }[] }
  MGCombIndex?: number
  MGReelIndex?: number[]
  MGLineSymbol?: RawSymbol[]
  MGLineCount?: number[]
  MGLineWin?: string[]
  MGMultiplier?: number
  MainWin?: string
}
interface RawBG {
  BGWheelPairIndex?: number
  BGInner?: number
  BGOuter?: number
  BonusWin?: string
}
interface RawSlotResult {
  Code?: number
  MGResult?: RawMG
  BGResult?: RawBG
  TotalWin?: string
  GameMode?: number
  BuyType?: number
}

const CONVERSION: protobuf.IConversionOptions = { longs: String, defaults: true, arrays: true }

export class RemoteSpinService implements SpinService {
  private readonly client: GameServerClient
  private readonly gameCmd: string

  constructor(client: GameServerClient, gameCmd = 'Slot001') {
    this.client = client
    this.gameCmd = gameCmd
  }

  async spin(request: SpinRequest): Promise<SlotResult> {
    // 遊戲層 SpinCmd：對齊 Unity（Slot001_Controller.SendSpin）帶 DebugCmd.Multiplier = -1。
    // proto 語意：-1 = 不指定，由後端正常流程產生；0 = 進副遊戲。
    // 若此處留空（不帶 DebugCmd），後端會把 Multiplier 取 proto3 預設值 0 = 強制進副遊戲，
    // 導致回傳盤面/倍數與正常局不同。TotalBet/BuyType 仍由 BaseSpinCmd 提供，此處不填。
    const spinType = spinCmdType()
    const spinValue = spinType.encode(spinType.create({ DebugCmd: { Multiplier: -1 } })).finish()

    const clientSpinType = gatewayType('ClientSpinCmd')
    const payload = clientSpinType
      .encode(
        clientSpinType.create({
          BaseSpinCmd: {
            TotalBet: toServerBalance(request.totalBet),
            BuyType: request.buyType,
            Currency: this.client.currency,
            AutoSpinId: 'Null',
            Turbo: 0,
            IsAuto: false,
            RefFreeVoucherId: '',
          },
          SpinData: { type_url: SPIN_DATA_TYPE_URL, value: spinValue },
        }),
      )
      .finish()

    const response = await this.client.request(this.gameCmd, payload)
    if (response.code !== 0) {
      throw new Error(`Spin 失敗 code=${response.code} ${response.message}`)
    }

    // ResponseInfo.Data 是 google.protobuf.Any（type_url + value）；先拆 Any 再解遊戲 SlotResult。
    // 對應 Unity ProtobufHelper.ParseAny<SlotResult>(ResponseInfo.Data)。
    const anyMsg = anyType().decode(response.data) as unknown as {
      type_url?: string
      value?: Uint8Array
    }
    const slotBytes = anyMsg.value ?? new Uint8Array(0)

    const resultType = slotResultType()
    let decoded: protobuf.Message
    try {
      decoded = resultType.decode(slotBytes)
    } catch (error) {
      const head = Array.from(slotBytes.slice(0, 80))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join(' ')
      logger.error(
        `SlotResult 解碼失敗（type_url=${anyMsg.type_url ?? ''} len=${slotBytes.length}）首80 bytes: ${head}`,
        error,
      )
      throw error
    }
    const raw = resultType.toObject(decoded, CONVERSION) as RawSlotResult
    // 帶入 envelope 的伺服器權威 spin 後餘額（ResponseInfo.Balance），供 GameController 校正。
    return { ...mapSlotResult(raw), serverBalance: response.balance }
  }
}

function toBig(value: string | number | undefined): bigint {
  if (value === undefined) return 0n
  return BigInt(typeof value === 'number' ? Math.trunc(value) : value)
}

function mapSlotResult(raw: RawSlotResult): SlotResult {
  const mg = raw.MGResult ?? {}
  const board = (mg.MGSymbol?.Reels ?? []).map((reel) =>
    (reel.Symbols ?? []).map((symbol) => symbol.Value ?? 0),
  )
  const mgResult: MGResult = {
    mgSymbol: board,
    mgCombIndex: mg.MGCombIndex ?? 0,
    mgReelIndex: mg.MGReelIndex ?? [],
    mgLineSymbol: (mg.MGLineSymbol ?? []).map((s) => s.Value ?? 0),
    mgLineCount: mg.MGLineCount ?? [],
    mgLineWin: (mg.MGLineWin ?? []).map(toBig),
    mgMultiplier: mg.MGMultiplier ?? 1,
    mainWin: toBig(mg.MainWin),
  }
  const bg = raw.BGResult ?? {}
  const bgResult: BGResult = {
    bgWheelPairIndex: bg.BGWheelPairIndex ?? 0,
    bgInner: bg.BGInner ?? 0,
    bgOuter: bg.BGOuter ?? 0,
    bonusWin: toBig(bg.BonusWin),
  }
  return {
    code: raw.Code ?? 0,
    mgResult,
    bgResult,
    totalWin: toBig(raw.TotalWin),
    gameMode: raw.GameMode ?? 0,
    buyType: raw.BuyType ?? 0,
  }
}
